import { Col, Form, TimePicker, Row } from "antd";
import dayjs from "dayjs";
import 'dayjs/locale/pt-br';
import debounce from "lodash/debounce";
import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { AiOutlineSearch } from "react-icons/ai";
import {
  TextInput,
  TextAreaInput,
  PasswordInput,
  NumberInput,
  Select,
  DateInput,
  PhoneInput,
  CPFInput,
  CNPJInput,
  CheckboxInput,
  CheckboxGroupInput,
  RadioInput,
  DateRangeInput,
  TreeSelectInput,
  Button,
  CepInput

} from '../ui';
import EditableSelect from '../ui/editableSelect';
import FileUpload from "../ui/file-upload";
import { useTheme } from '../../contexts/ThemeContext';
import Loading from "../loading/Loading";
import Api from "../../services/api";


dayjs.locale("pt-br");

// removed unused Option

// Funções de validação de CPF e CNPJ permanecem iguais
const validateCPF = (value) => {
  const cpf = value.replace(/\D/g, "");
  if (cpf.length !== 11) return false;
  let sum = 0;
  let remainder;
  for (let i = 1; i <= 9; i++)
    sum += parseInt(cpf.substring(i - 1, i)) * (11 - i);
  remainder = (sum * 10) % 11;
  if (remainder === 10 || remainder === 11) remainder = 0;
  if (remainder !== parseInt(cpf.substring(9, 10))) return false;
  sum = 0;
  for (let i = 1; i <= 10; i++)
    sum += parseInt(cpf.substring(i - 1, i)) * (12 - i);
  remainder = (sum * 10) % 11;
  if (remainder === 10 || remainder === 11) remainder = 0;
  return remainder === parseInt(cpf.substring(10, 11));
};

const validateCNPJ = (value) => {
  const cnpj = value.replace(/\D/g, "");
  if (cnpj.length !== 14) return false;
  let length = cnpj.length - 2;
  let numbers = cnpj.substring(0, length);
  let digits = cnpj.substring(length);
  let sum = 0;
  let pos = length - 7;
  for (let i = length; i >= 1; i--) {
    sum += numbers.charAt(length - i) * pos--;
    if (pos < 2) pos = 9;
  }
  let result = sum % 11 < 2 ? 0 : 11 - (sum % 11);
  if (result !== parseInt(digits.charAt(0))) return false;
  length = length + 1;
  numbers = cnpj.substring(0, length);
  sum = 0;
  pos = length - 7;
  for (let i = length; i >= 1; i--) {
    sum += numbers.charAt(length - i) * pos--;
    if (pos < 2) pos = 9;
  }
  result = sum % 11 < 2 ? 0 : 11 - (sum % 11);
  return result === parseInt(digits.charAt(1));
};

// Função para validação robusta de senhas
const validatePassword = (password, options = {}) => {
  const {
    minLength = 8,
    requireUppercase = true,
    requireLowercase = true,
    requireNumbers = true,
    requireSpecialChars = true,
    customMessage = false
  } = options;

  const errors = [];

  if (password.length < minLength) {
    errors.push(`pelo menos ${minLength} caracteres`);
  }

  if (requireUppercase && !/[A-Z]/.test(password)) {
    errors.push('uma letra maiúscula');
  }

  if (requireLowercase && !/[a-z]/.test(password)) {
    errors.push('uma letra minúscula');
  }

  if (requireNumbers && !/\d/.test(password)) {
    errors.push('um número');
  }

  if (requireSpecialChars && !/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
    errors.push('um caractere especial (!@#$%^&*()_+-=[]{}|;:,.<>?)');
  }

  if (errors.length > 0) {
    if (customMessage) {
      return {
        isValid: false,
        message: customMessage
      };
    }
    
    const message = `A senha deve conter ${errors.join(', ')}.`;
    return {
      isValid: false,
      message: message
    };
  }

  return {
    isValid: true,
    message: 'Senha válida'
  };
};

/**
 * DynamicForm - Componente de formulário dinâmico avançado
 * 
 * Novas funcionalidades:
 * - loading: Mostra loading no botão de submit
 * - disabled: Desabilita o botão de submit
 * - confirmPassword: Campo para confirmação de senha com validação automática
 * - Validação robusta de senhas (maiúscula, minúscula, número, caractere especial)
 * - Validações automáticas para CPF, CNPJ, email, telefone
 * - Validações customizadas por campo
 * - Scroll automático para campos com erro
 * - Melhor tratamento de erros de validação
 * 
 * Exemplo de uso do confirmPassword:
 * 
 * // Formulário de criação de usuário
 * const formConfig = [{
 *   columns: 1,
 *   questions: [
 *     {
 *       id: 'username',
 *       label: 'Nome de Usuário',
 *       type: 'text',
 *       required: true
 *     },
 *     {
 *       id: 'password',
 *       label: 'Senha',
 *       type: 'password',
 *       required: true,
 *       minLength: 8, // padrão: 8
 *       requireUppercase: true, // padrão: true - exige letra maiúscula
 *       requireLowercase: true, // padrão: true - exige letra minúscula
 *       requireNumbers: true, // padrão: true - exige números
 *       requireSpecialChars: true, // padrão: true - exige caracteres especiais
 *       customValidationMessage: 'Mensagem customizada opcional'
 *     },
 *     {
 *       id: 'confirmPassword',
 *       label: 'Confirmar Senha',
 *       type: 'confirmPassword',
 *       required: true,
 *       compareField: 'password' // opcional, padrão é 'password'
 *     }
 *   ]
 * }];
 * 
 * // Para senhas mais simples (ex: senha temporária):
 * {
 *   id: 'tempPassword',
 *   label: 'Senha Temporária',
 *   type: 'password',
 *   required: true,
 *   minLength: 6,
 *   requireUppercase: false,
 *   requireSpecialChars: false
 * }
 * 
 * // Para formulários de alteração de senha com campo diferente:
 * {
 *   id: 'newPassword',
 *   label: 'Nova Senha',
 *   type: 'password',
 *   required: true,
 *   minLength: 8
 * },
 * {
 *   id: 'confirmNewPassword',
 *   label: 'Confirmar Nova Senha',
 *   type: 'confirmPassword',
 *   required: true,
 *   compareField: 'newPassword' // compara com 'newPassword' ao invés de 'password'
 * }
 * 
 * @param {Object} props - Propriedades do componente
 * @param {Array} props.formConfig - Configuração dos campos do formulário
 * @param {Object} props.values - Valores atuais do formulário
 * @param {Function} props.setValues - Função para atualizar valores
 * @param {string} props.submitText - Texto do botão de submit
 * @param {Function} props.onSubmit - Função executada no submit
 * @param {boolean} props.loading - Estado de loading do formulário
 * @param {boolean} props.disabled - Estado desabilitado do formulário
 */
export const DynamicForm = ({
  formConfig,
  values,
  setValues,
  submitText = "Enviar",
  onSubmit,
  isSignIn = false,
  onClose,
  submitOnSide = false,
  className = '',
  paddingWidth,
  inputPadding,
  style = {},
  extraFooterContent,
  submitButtonStyle,
  instantUpdate = false,
  isFilter = false,
  debounceTime = 500,
  loading = false,
  disabled = false
}) => {
  const [form] = Form.useForm();
  const { theme } = useTheme();
  const filterInitRef = useRef(false);

  // Debounce unificado para filtros - otimizado com lodash
  const debouncedSetValues = useMemo(
    () =>
      isFilter
        ? debounce(() => {
            const nextValues = form.getFieldsValue(true);
            setValues(nextValues);
          }, debounceTime)
        : debounce((id, inputValue) => {
            setValues((prevValues) => ({ ...prevValues, [id]: inputValue }));
          }, instantUpdate ? debounceTime : 500),
    [debounceTime, form, instantUpdate, isFilter, setValues]
  );

  // Cleanup do debounce quando o componente for desmontado
  useEffect(() => {
    return () => {
      debouncedSetValues.cancel();
    };
  }, [debouncedSetValues]);

  const handleInputChange = useCallback(
    (id, inputValue) => {
      // Função auxiliar para comparar valores de forma mais robusta
      const valuesAreEqual = (val1, val2) => {
        // Se o novo valor é undefined/null e o anterior tinha algum valor, é uma mudança
        if ((val2 === undefined || val2 === null) && (val1 !== undefined && val1 !== null && val1 !== '' && !(Array.isArray(val1) && val1.length === 0))) {
          return false;
        }

        // Se ambos são undefined ou null
        if ((val1 === undefined || val1 === null) && (val2 === undefined || val2 === null)) {
          return true;
        }

        // Se ambos são arrays vazios
        if (Array.isArray(val1) && Array.isArray(val2) && val1.length === 0 && val2.length === 0) {
          return true;
        }

        // Se um é array vazio e o outro é undefined/null
        if ((Array.isArray(val1) && val1.length === 0 && (val2 === undefined || val2 === null)) ||
            (Array.isArray(val2) && val2.length === 0 && (val1 === undefined || val1 === null))) {
          return true;
        }

        // Se ambos são strings vazias
        if (val1 === '' && val2 === '') {
          return true;
        }

        // Comparação normal
        return val1 === val2;
      };

      // Sempre processar a mudança se os valores são diferentes
      if (!valuesAreEqual(values?.[id], inputValue)) {
        // Para filtros ou instantUpdate, usar debounce
        if (isFilter) {
          debouncedSetValues();
        } else if (instantUpdate) {
          debouncedSetValues(id, inputValue);
        } else {
          // Para modo normal sem debounce
          setValues((prevValues) => {
            const newValues = { ...prevValues, [id]: inputValue };
            return newValues;
          });
        }
        form.setFieldValue(id, inputValue);
        
        // Re-validar campos de confirmPassword relacionados quando senha principal muda
        formConfig.forEach(section => {
          section.questions.forEach(question => {
            if (question.type === 'confirmPassword') {
              const compareField = question.compareField || 'password';
              if (id === compareField) {
                // Re-validar o campo de confirmação
                form.validateFields([question.id]).catch(() => {
                  // Ignorar erros de validação aqui, pois são esperados durante a digitação
                });
              }
            }
          });
        });
      }
    },
    [values, debouncedSetValues, form, isFilter, instantUpdate, setValues, formConfig]
  );

  useEffect(() => {
    if (!values) return;
    if (Object.keys(values).length === 0) {
      form.resetFields();
      filterInitRef.current = false;
      return;
    }

    if (isFilter && filterInitRef.current) return;

    const formattedValues = { ...values };

    formConfig.forEach(section => {
      section.questions.forEach(question => {
        if (["date", "datetime", "time", "range-date"].includes(question.type)) {
          if (formattedValues[question.id]) {
            if (question.type === "range-date") {
              formattedValues[question.id] = [
                dayjs(formattedValues[question.id][0]),
                dayjs(formattedValues[question.id][1])
              ];
            } else {
              if (!dayjs.isDayjs(formattedValues[question.id])) {
                formattedValues[question.id] = dayjs(formattedValues[question.id]);
              }
            }
          }
        }
      });
    });

    form.setFieldsValue(formattedValues);
    if (isFilter) {
      filterInitRef.current = true;
    }
  }, [form, values, formConfig, isFilter]);

  const renderInput = (question) => {
    const {
      type,
      id,
      placeholder = "",
      
      left = "",
      format,
      precision = 2,
      step = 0.01,
      idSync = null,
      options = [],
      paddingWidth,
      treeData = [],
      style: inputStyle = {},
      className: inputClassName = '',
      prefixIcon,
      width,
      disabled = false,
      onKeyPress,
      onCreateNew,
      onSelect,
      showSearch
    } = question;

    // Estilo base simplificado
    const baseStyle = {
      width: isFilter ? '100%' : (width || '100%'),
      ...inputStyle
    };
    
    // Função getValue simplificada
    const getValue = () => {
      const formValue = form.getFieldValue(id);
      if (isFilter && formValue !== undefined) return formValue;

      // Para selects e multiselects, tratar valores vazios adequadamente
      if (type === 'select' || type === 'multiselect') {
        const currentValue = values[id];
        
        // Se o valor é undefined, null, string vazia ou array vazio, retornar undefined
        if (currentValue === undefined || currentValue === null || currentValue === '' || 
            (Array.isArray(currentValue) && currentValue.length === 0)) {
          return undefined;
        }
        return currentValue;
      }
      
      // Para outros inputs, usar null/undefined check em vez de falsy check
      return values[id] ?? '';
    };

    // Props comuns simplificadas - sempre usa onChange
    const commonProps = {
      id,
      value: getValue(),
      onChange: (value) => {
        handleInputChange(id, value);
      },
      onKeyPress: onKeyPress,
      placeholder,
      prefix: prefixIcon || (type === 'text' && isFilter ? <AiOutlineSearch style={{ color: '#999' }} /> : undefined),
      style: baseStyle,
      className: inputClassName,
      disabled: disabled,
      showSearch: showSearch
    };
    

    switch (type) {
      case "textarea":
        return (
          <TextAreaInput
            {...commonProps}
            autoSize={{ minRows: 3, maxRows: 6 }}
          />
        );
      case "password":
        return (
          <PasswordInput
            {...commonProps}
            autoComplete={question.autoComplete}
            name={question.name}
            showStrengthIndicator={question.showStrengthIndicator !== false}
            strengthIndicatorStyle={question.strengthIndicatorStyle}
          />
        );
      case "confirmPassword":
        return (
          <PasswordInput
            {...commonProps}
            autoComplete={question.autoComplete}
            name={question.name}
            //placeholder={placeholder || "Confirme sua senha"}
            showStrengthIndicator={false}
          />
        );
      case "integer":
        return (
          <NumberInput
            {...commonProps}
            type="number"
            step={1}
          />
        );
      case "decimal":
        return (
          <NumberInput
            {...commonProps}
            type="number"
            step={step}
            precision={precision}
          />
        );
      case "currency":
        return (
          <NumberInput
            {...commonProps}
            type="number"
            step={step}
            precision={precision}
            prefix={left}
          />
        );
      case "datetime":
        return (
          <DateInput
            {...commonProps}
            format={format}
            showTime
          />
        );
      case "date":
        return (
          <DateInput
            {...commonProps}
            format={format || "DD/MM/YYYY"}
          />
        );
      case "time":
        return (
          <TimePicker
            autoComplete="off"
            placeholder={placeholder}
            value={dayjs.isDayjs(values[id]) ? values[id] : dayjs(values[id])}
            onChange={(time) => handleInputChange(id, time)}
            style={baseStyle}
            format={format}
          />
        );
      case "select":
        // Se não há opções e não está carregando, mostrar mensagem
        if (!options || options.length === 0) {
          if (question.loading) {
            return (
              <Select
                {...commonProps}
                options={[]}
                loading={true}
                disabled={true}
                placeholder="Carregando..."
                allowClear={isFilter}
              />
            );
          } else {
            return (
              <Select
                {...commonProps}
                options={[]}
                disabled={true}
                placeholder="Nenhuma opção disponível"
                allowClear={isFilter}
              />
            );
          }
        }
        
        return (
          <Select
            {...commonProps}
            options={options}
            loading={question.loading}
            disabled={question.disabled || question.loading}
            allowClear={isFilter}
          />
        );
      case "multiselect":
        return (
          <Select
            {...commonProps}
            options={options}
            mode="multiple"
            allowClear={isFilter}
          />
        );
      case "phone":
        return (
          <PhoneInput
            {...commonProps}
          />
        );
      case "checkbox":
        return (
          <CheckboxInput
            {...commonProps}
          />
        );
      case "radio":
        return (
          <RadioInput
            {...commonProps}
            options={options}
          />
        );
      case "email":
        return (
          <TextInput
            {...commonProps}
            type="email"
          />
        );
      case "cpf":
        return (
          <CPFInput
            {...commonProps}
          />
        );
        case "cep":
        return (
          <CepInput
            {...commonProps}
          />
        );
      case "cnpj":
        return (
          <CNPJInput
            {...commonProps}
          />
        );
      case "checkbox-group":
        return (
          <CheckboxGroupInput
            {...commonProps}
            options={options}
          />
        );
      case "range-date":
        return (
          <DateRangeInput
            {...commonProps}
          />
        );
      case "tree-select":
        return (
          <TreeSelectInput
            {...commonProps}
            treeData={treeData}
          />
        );
      case "images":
        return (
          <FileUpload
            onFileUpload={(uploadedFiles) => handleInputChange(id, uploadedFiles)}
            preUploadedFiles={values[id]}
            inputType="images"
          />
        );
      case "files":
        return (
          <FileUpload
            onFileUpload={(uploadedFiles) => handleInputChange(id, uploadedFiles)}
            preFilesIds={values[id]}
            inputType="files"
          />
        );
      case "editableSelect":
        
        
        return (
          <EditableSelect
            {...commonProps}
            options={options}
            onCreateNew={onCreateNew}
            onSelect={onSelect}
          />
        );
      case "excelUpload":
        const fileValue = values[id];
        const loading = question.loading || false;
        const hasFile = (typeof fileValue === 'string' && fileValue.trim() !== '') || (Array.isArray(fileValue) && fileValue.length > 0);
        const handleExcelDownload = async () => {
          let nomeArquivo = '';
          if (typeof fileValue === 'string') {
            nomeArquivo = fileValue;
          } else if (Array.isArray(fileValue) && fileValue[0]) {
            nomeArquivo = fileValue[0];
          }
          if (!nomeArquivo) return;
          try {
            const response = await Api.get(`/sync/getFile/${encodeURIComponent(nomeArquivo)}`);
            const url = response.data?.url;
            if (!url) {
              alert('URL de download não encontrada');
              return;
            }
            const a = document.createElement('a');
            a.href = url;
            a.download = nomeArquivo;
            document.body.appendChild(a);
            a.click();
            a.remove();
          } catch (err) {
            alert('Erro ao baixar arquivo');
          }
        };
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {hasFile && (
              <Button
                type="default"
                onClick={handleExcelDownload}
                style={{ marginRight: 8 ,minWidth: 180}}
                disabled={loading}
              >
                {loading ? <Loading size="small" tip="" style={{ padding: 0, margin: 0 }} /> : 'Download'}
              </Button>
            )}
            <FileUpload
              baseUrl="sync"
              inputType="excel"
              onFileUpload={(files) => {
                const nomeArquivo = Array.isArray(files) ? files[0] : files;
                if (nomeArquivo) handleInputChange(id, nomeArquivo);
              }}
              accept=".xlsx,.xls,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv"
              title={hasFile ? 'Trocar arquivo' : 'Escolher arquivo'}
              description=""
              style={{ minWidth: 180, margin: 0 }}
              hideUploadWhenHasFiles={true}
              variant="default"
              simpleButtonOnly={true}
              idSync={idSync}
              loading={loading}
              singleFile={true}
            />
          </div>
        );
      default:
        return (
          <TextInput
            {...commonProps}
            type={type}
          />
        );
    }
  };

  const getValidationRules = (question) => {
    const rules = [];

    // Regra de obrigatório SEMPRE PRIMEIRO
    if (question.required) {
      rules.push({
        required: true,
        message: `Por favor, preencha o campo ${question.label}!`,
      });
    }

    // Validações específicas por tipo - APENAS se o campo não estiver vazio
    switch (question.type) {
      case "email":
        rules.push({
          validator: (_, value) => {
            if (!value) return Promise.resolve(); // Se vazio, deixa o required tratar
            if (value && !/\S+@\S+\.\S+/.test(value)) {
              return Promise.reject(new Error('Por favor, insira um e-mail válido!'));
            }
            return Promise.resolve();
          },
        });
        break;
      case "cpf":
        rules.push({
          validator: (_, value) => {
            if (!value) return Promise.resolve(); // Se vazio, deixa o required tratar
            if (validateCPF(value)) {
              return Promise.resolve();
            }
            return Promise.reject(new Error('CPF inválido!'));
          },
        });
        break;
      case "cnpj":
        rules.push({
          validator: (_, value) => {
            if (!value) return Promise.resolve(); // Se vazio, deixa o required tratar
            if (validateCNPJ(value)) {
              return Promise.resolve();
            }
            return Promise.reject(new Error('CNPJ inválido!'));
          },
        });
        break;
      case "password":
        // Validação robusta de senha - APENAS se tiver valor
        rules.push({
          validator: (_, value) => {
            if (!value) return Promise.resolve(); // Se vazio, deixa o required tratar
            
            // Configurações de validação baseadas nas props do campo
            const passwordOptions = {
              minLength: question.minLength || 8,
              requireUppercase: question.requireUppercase !== false,
              requireLowercase: question.requireLowercase !== false,
              requireNumbers: question.requireNumbers !== false,
              requireSpecialChars: question.requireSpecialChars !== false,
              customMessage: question.customValidationMessage
            };

            
            const validation = validatePassword(value, passwordOptions);
            
            if (validation.isValid) {
              return Promise.resolve();
            }
            return Promise.reject(new Error(validation.message));
          },
        });
        break;
      case "confirmPassword":
        rules.push({
          validator: (_, value) => {
            if (!value) return Promise.resolve(); // Se vazio, deixa o required tratar
            
            // Campo a ser comparado (padrão: 'password')
            const compareField = question.compareField || 'password';
            const passwordValue = form.getFieldValue(compareField);
            
            if (value === passwordValue) {
              return Promise.resolve();
            }
            return Promise.reject(new Error('As senhas não coincidem!'));
          },
        });
        break;
      case "phone":
        rules.push({
          validator: (_, value) => {
            if (!value) return Promise.resolve(); // Se vazio, deixa o required tratar
            // Remove caracteres não numéricos
            const cleanValue = value.replace(/\D/g, '');
            if (cleanValue.length >= 10 && cleanValue.length <= 11) {
              return Promise.resolve();
            }
            return Promise.reject(new Error('Telefone inválido!'));
          },
        });
        break;
      default:
        break;
    }

    // Validações customizadas adicionais (não substituem as validações padrão)
    if (question.customValidation) {
      // Também devem verificar se o campo não está vazio primeiro
      const customValidationWrapper = {
        validator: async (rule, value) => {
          if (!value) return Promise.resolve(); // Se vazio, deixa o required tratar
          return question.customValidation.validator(rule, value);
        },
      };
      rules.push(customValidationWrapper);
    }

    return rules;
  };

  const renderQuestions = (questions, columns) => {
    // Para filtros, renderizar em linha
    if (isFilter) {
      return (
        <div style={{ 
          display: 'flex', 
          gap: '12px', 
          width: '100%',
          alignItems: 'flex-end'
        }}>
          {questions.map((question, index) => {
            // Definir larguras mais simples
            let itemStyle = {
              flexShrink: 0,
              minWidth: question.minWidth || 0
            };

            // Aplicar larguras baseadas na prop width
            if (question.width === '55%') {
              itemStyle.width = 'calc(55% - 8px)';
            } else if (question.width === '22.5%') {
              itemStyle.width = 'calc(22.5% - 4px)';
            } else if (question.width === '60%') {
              itemStyle.width = 'calc(60% - 8px)';
            } else if (question.width === '20%') {
              itemStyle.width = 'calc(20% - 4px)';
            } else if (question.width && question.width.includes('%')) {
              // Para outras porcentagens, calcular automaticamente
              const percentage = parseFloat(question.width);
              const adjustment = percentage > 50 ? 8 : 4;
              itemStyle.width = `calc(${question.width} - ${adjustment}px)`;
            } else if (question.width) {
              itemStyle.width = question.width;
            } else {
              itemStyle.flex = 1;
            }

            return (
              <div key={index} style={itemStyle}>
                <Form.Item
                  name={question.id}
                  label={question.label}
                  style={{ marginBottom: 0 }}
                  rules={getValidationRules(question)}
                >
                  {renderInput(question)}
                </Form.Item>
              </div>
            );
          })}
        </div>
      );
    }

    // Renderização normal com colunas
    const colSpan = 24 / columns;

    return (
      <Row gutter={[16, 16]}>
        {questions.map((question, index) => (
          <Col key={index} span={colSpan}>
            <Form.Item
              name={question.id}
              label={question.label}
              rules={getValidationRules(question)}
              validateStatus={question.validateStatus}
              help={question.help}
            >
              {renderInput(question)}
            </Form.Item>
          </Col>
        ))}
      </Row>
    );
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (onSubmit) {
        await onSubmit(values);
      }
    } catch (errorInfo) {
      // Se houver erros de validação, o form já mostra as mensagens
      console.log('Erro de validação:', errorInfo);
      
      // Scroll para o primeiro campo com erro
      const firstErrorField = errorInfo?.errorFields?.[0];
      if (firstErrorField) {
        const fieldName = firstErrorField.name[0];
        const fieldElement = document.querySelector(`[id="${fieldName}"]`);
        if (fieldElement) {
          fieldElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          fieldElement.focus();
        }
      }
    }
  };

  // Para filtros, não mostrar botão de submit
  if (isFilter && !onSubmit) {
    return (
      <Form
        form={form}
        layout="vertical"
        className={`custom-form ${className}`}
        style={{
          //margin: '12px',
          padding: '12px',
          ...style,
          color: theme.textSecondary
        }}
      >
        {formConfig?.map((section, index) => (
          <div key={index}>
            {renderQuestions(section.questions, section.columns)}
          </div>
        ))}
      </Form>
    );
  }

  return (
    <Form
      form={form}
      layout="vertical"
      
      className={`custom-form ${className}`}
      style={{
        ...(isFilter ? {
          margin: '-12px',
          padding: '12px'
        } : {}),
        ...style,
        color: theme.textSecondary
      }}
    >
      {submitOnSide ? (
        <Row gutter={16}>
          <Col flex="auto">
            {formConfig?.map((section, index) => (
              <div key={index}>
                {section.title && (
                  <h3 style={{
                    marginTop: index > 0 ? 25 : 0,
                    marginBottom: 5,
                    color: theme.textSecondary
                  }}>
                    {section.title}
                  </h3>
                )}
                {renderQuestions(section.questions, section.columns)}
              </div>
            ))}
          </Col>
          <Col>
            <Button 
              type="primary" 
              onClick={handleSubmit}
              loading={loading}
              disabled={disabled}
            >
              {submitText}
            </Button>
          </Col>
        </Row>
      ) : (
        <>
          {formConfig?.map((section, index) => (
            <div key={index}>
              {section.title && (
                <h3 style={{
                  marginTop: index > 0 ? 25 : 0,
                  marginBottom: 5,
                  color: theme.textSecondary
                }}>
                  {section.title}
                </h3>
              )}
              {renderQuestions(section.questions, section.columns)}
            </div>
          ))}
          {onSubmit && (
            <Row gutter={16} style={{width: '100%', gap: 8}} justify={extraFooterContent ? 'space-between' : 'end'} >
              {extraFooterContent && (
                <Col >
                  {extraFooterContent}
                </Col>
              )}
              {onClose && (
                <Col style={{display: 'flex', justifyContent: 'flex-start'}}>
                  <Button onClick={onClose}>
                    Fechar
                  </Button>
                </Col>
              )}
              <Col style={{display: 'flex', justifyContent: 'flex-end'}} >
                <Button
                  type="primary"
                  onClick={handleSubmit}
                  loading={loading}
                  disabled={disabled}
                  style={submitButtonStyle || {
                    backgroundColor: theme.primary,
                    color: theme.white,
                    border: 'none',
                    borderRadius: '5px',
                  }}
                >
                  {submitText}
                </Button>
              </Col>
            </Row>
          )}
        </>
      )}
    </Form>
  );
};
